import { useCallback, useMemo } from "react";
import _ from "lodash";

import { CloseIcon, DescriptionOutlinedIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/Dialog";
import IconButton from "components/IconButton";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import Tooltip from "components/Tooltip";
import UploadButton from "components/UploadButton";
import { useLoadingBackdrop } from "components/useLoadingBackdrop";

import { useSignedUser } from "entities/Auth/sdk";
import { DealImportFileUploadButton } from "entities/Deal/components/DealImportFileUpload/DealImportFileUploadButton";
import {
  useDealDetails,
  useOnboardingChecklistFileFieldsWithoutValues,
} from "entities/Deal/sdk";
import { upload } from "entities/File/sdk";
import { superuserChecklistFileImportCreate } from "entities/Superuser/sdk";

export interface IFilesUploadDialog {
  handleFilesUpload: ({
    checklistFileImportId,
    categorize,
  }: {
    checklistFileImportId: number;
    categorize: boolean;
  }) => void;
  dealId: number;
  open: boolean;
  onClose: () => void;
  selectedFiles: Array<File>;
  handleFilesSelect: ({ files }: { files: Array<File> }) => void;
  clearFile: ({ fileToRemove }: { fileToRemove: File }) => void;
}

const FilesUploadDialog = ({
  handleFilesUpload,
  dealId,
  open,
  onClose,
  selectedFiles,
  handleFilesSelect,
  clearFile,
}: IFilesUploadDialog) => {
  const [showLoading, hideLoading] = useLoadingBackdrop();

  const { data: user } = useSignedUser();
  const { data: deal, isValidating: isFetchingDeal } = useDealDetails({
    dealId,
  });
  const {
    data: fileFields,
    isValidating: isFetchingOnboardingChecklistFileFieldsWithoutValues,
  } = useOnboardingChecklistFileFieldsWithoutValues({
    dealId,
    enabled: open,
  });

  const handleChecklistFileImportCreate = useCallback(
    ({ categorize }: { categorize: boolean }) => {
      if (!_.isNil(dealId) && !_.isNil(user?.id)) {
        showLoading();

        const fileIds: Array<number> = [];
        const uploadPromises = selectedFiles.map((file) =>
          upload({ file, isPrivate: true })
            .then((fileId) => {
              fileIds.push(fileId);
            })
            .catch(handleInvalidRequest)
        );

        Promise.all(uploadPromises).then(() => {
          superuserChecklistFileImportCreate({
            dealId,
            fileIds,
            valueCreatorId: user.id,
            useOnboardingCompanyPackage: true,
          })
            .then(({ id }) => {
              handleFilesUpload({ checklistFileImportId: id, categorize });
            })
            .finally(() => {
              hideLoading();
            });
        });
      }
    },
    [
      selectedFiles,
      dealId,
      user?.id,
      showLoading,
      hideLoading,
      handleFilesUpload,
    ]
  );

  const shouldSubmitButtonsBeDisabled = useMemo(
    () =>
      _.isNil(fileFields) ||
      isFetchingDeal ||
      isFetchingOnboardingChecklistFileFieldsWithoutValues ||
      _.isEmpty(selectedFiles),
    [
      fileFields,
      selectedFiles,
      isFetchingDeal,
      isFetchingOnboardingChecklistFileFieldsWithoutValues,
    ]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          margin: 2,
          padding: 2,
          borderRadius: theme.spacing(1),
          backgroundColor: colors.blue20,
        }}
        flexDirection="row"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Text variant="h1" fontWeight={700}>
          + Upload documents
        </Text>
        <Text variant="text2" fontWeight={700} maxWidth={190} noWrap>
          {deal?.name}
        </Text>
      </DialogTitle>

      <DialogContent
        sx={{
          height: "400px",
          paddingX: 2,
          paddingY: 0,
          flexGrow: 1,
          minHeight: "110px",
          display: "grid",
          gridGap: theme.spacing(2),
          gridTemplateColumns: "1.5fr 1fr 1fr",
        }}
      >
        <UploadButton
          onChange={handleFilesSelect}
          renderComponent={(state) => (
            <Stack height="100%">
              <DealImportFileUploadButton state={state} />
            </Stack>
          )}
        />
        <Stack
          spacing={2}
          padding={theme.spacing(0, 2, 2, 2)}
          sx={{
            border: `1px dotted ${colors.gray40}`,
            borderRadius: "8px",
            maxHeight: "inherit",
            overflowY: "auto",
          }}
        >
          <Stack
            sx={{
              position: "sticky",
              top: 0,
              backgroundColor: colors.white,
              paddingTop: 2,
              zIndex: 2,
            }}
          >
            <Text
              sx={{
                backgroundColor: colors.gray20,
                padding: theme.spacing(0.25, 0.5),
                width: "fit-content",
                fontSize: 12,
                fontWeight: 500,
                textAlign: "start",
              }}
            >
              {`${selectedFiles.length} FILE${selectedFiles.length === 1 ? "" : "S"} SELECTED TO UPLOAD`}
            </Text>
          </Stack>

          <Stack spacing={1}>
            {selectedFiles.map((file, index) => (
              <Stack
                spacing={1}
                key={`${file.name}-${index}`}
                alignItems="center"
                justifyContent="space-between"
                direction="row"
                sx={{
                  transition: "background-color 0.2s ease",
                  "&:hover": {
                    backgroundColor: colors.gray5,
                  },
                }}
              >
                <Stack flexDirection="row" alignItems="center" width="80%">
                  <Tooltip title={file.name} enterDelay={1000}>
                    <Stack
                      flexDirection="row"
                      alignItems="center"
                      width="100%"
                      gap={1}
                    >
                      <DescriptionOutlinedIcon sx={{ height: "25px" }} />

                      <Text
                        variant="text2"
                        whiteSpace="nowrap"
                        textOverflow="ellipsis"
                        overflow="hidden"
                      >
                        {file.name}
                      </Text>
                    </Stack>
                  </Tooltip>
                </Stack>
                <IconButton onClick={() => clearFile({ fileToRemove: file })}>
                  <CloseIcon />
                </IconButton>
              </Stack>
            ))}
            {_.isEmpty(selectedFiles) && (
              <Text variant="text2" color={colors.gray60} paddingTop={1}>
                No files uploaded...
              </Text>
            )}
          </Stack>
        </Stack>
        <Stack
          spacing={2}
          padding={theme.spacing(0, 2, 2, 2)}
          sx={{
            border: `1px dotted ${colors.gray40}`,
            borderRadius: "8px",
            maxHeight: "inherit",
            overflowY: "auto",
          }}
        >
          <Stack
            sx={{
              position: "sticky",
              top: 0,
              backgroundColor: colors.white,
              paddingTop: 2,
              zIndex: 2,
            }}
          >
            {_.isNil(fileFields) ? (
              <Skeleton variant="rectangular" width={173} height={22} />
            ) : (
              <Text
                sx={{
                  backgroundColor: colors.gray20,
                  padding: theme.spacing(0.25, 0.5),
                  width: "fit-content",
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                {`${fileFields?.length} MISSING
              DOCUMENT TYPE${fileFields?.length === 1 ? "" : "S"}`}
              </Text>
            )}
          </Stack>

          {!_.isEmpty(fileFields) &&
            !isFetchingOnboardingChecklistFileFieldsWithoutValues && (
              <Stack spacing={1}>
                {fileFields?.map((fileField) => (
                  <Stack
                    spacing={1}
                    key={`${fileField.name}-${fileField.id}`}
                    alignItems="center"
                    justifyContent="space-between"
                    direction="row"
                    sx={{
                      animation: "appear 0.3s linear",
                      "@keyframes appear": {
                        from: {
                          opacity: 0,
                        },
                        to: { opacity: 1 },
                      },
                    }}
                  >
                    <Tooltip title={fileField.name} enterDelay={1000}>
                      <Text
                        variant="text2"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {fileField.name}
                      </Text>
                    </Tooltip>
                  </Stack>
                ))}
              </Stack>
            )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ padding: 2 }}>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>

        <Button
          color="secondary"
          disabled={shouldSubmitButtonsBeDisabled}
          onClick={() => handleChecklistFileImportCreate({ categorize: false })}
        >
          Upload only
        </Button>

        <Button
          color="primary"
          disabled={shouldSubmitButtonsBeDisabled}
          onClick={() => handleChecklistFileImportCreate({ categorize: true })}
        >
          Upload & categorize now
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilesUploadDialog;
